import React, { useState, useEffect, useRef, startTransition, useCallback } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import PaginationControls from './PaginationControls';
import { useTranslation } from 'react-i18next';

/**
 * Reusable data table component using HTML <table> for automatic column sizing.
 *
 * Matches the project's existing list view styling (background, borders, hover,
 * typography) but replaces the fragile CSS-grid approach with a real table so
 * columns size themselves based on content.
 *
 * Usage:
 *   <DataTable
 *     columns={[
 *       { key: 'name', label: 'Name', render: (row) => row.name },
 *       { key: 'price', label: 'Price', align: 'right', render: (row) => formatPrice(row.price) },
 *       { key: 'date', label: 'Date', sortable: true, sortField: 'created_at', render: (row) => formatDate(row.date) },
 *     ]}
 *     data={rows}
 *     loading={loading}
 *     onRowClick={(row) => openDetail(row)}
 *     sortField={sortField}
 *     sortDirection={sortDirection}
 *     onSort={handleSort}
 *     emptyIcon={<InboxIcon />}
 *     emptyTitle="No items"
 *     emptyDescription="Items will appear here."
 *     pagination={{ page, totalPages, totalCount, pageSize, onPageChange, onPageSizeChange }}
 *   />
 *
 * Column definition:
 *   key:        string  — unique column identifier
 *   label:      string  — header text (will be uppercased)
 *   render:     (row, index) => ReactNode — cell content
 *   align:      'left' | 'right' | 'center' (default: 'left')
 *   sortable:   boolean — show sort controls in header
 *   sortField:  string  — field name sent to onSort (defaults to key)
 *   width:      string  — optional CSS width hint (e.g. '1%' for shrink-to-fit)
 *   maxWidth:   number|string — optional max width to cap column expansion (e.g. 350 or '350px')
 *   noWrap:     boolean — prevent wrapping in this column (default: false)
 *   hideBelow:  string  — MUI breakpoint at which to show (e.g. 'lg' hides on md, shows on lg+)
 *
 * Selection props (all optional):
 *   selectable:       boolean — enable row selection checkboxes
 *   selectedIds:      Set     — set of currently selected row IDs
 *   onSelectionChange: (Set) => void — callback when selection changes
 *   rowIdField:       string  — field to use as row ID (default: 'id')
 *   selectionActions: Array<{ label, icon?, onClick, color?, variant?, disabled?, tooltip? }>
 */

const headerCellSx = {
  fontWeight: 600,
  fontSize: '12px',
  color: 'text.secondary',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
};

const SortableHeaderCell = ({ label, field, currentSort, currentDirection, onSort, align = 'left' }) => {
  const isActive = currentSort === field;

  return (
    <Box
      onClick={() => onSort(field)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        cursor: 'pointer',
        userSelect: 'none',
        justifyContent: align === 'right' ? 'flex-end' : align === 'center' ? 'center' : 'flex-start',
        '&:hover .header-label': {
          color: 'text.primary',
        },
      }}
    >
      <Typography
        className="header-label"
        sx={{
          ...headerCellSx,
          color: isActive ? 'secondary.main' : 'text.secondary',
          transition: 'color 0.15s ease',
        }}
      >
        {label}
      </Typography>
      {isActive && (
        currentDirection === 'asc'
          ? <ArrowUpwardIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
          : <ArrowDownwardIcon sx={{ fontSize: 14, color: 'secondary.main' }} />
      )}
    </Box>
  );
};

const DataTable = ({
  columns,
  data,
  loading = false,
  onRowClick,
  sortField,
  sortDirection,
  onSort,
  emptyIcon,
  emptyTitle,
  emptyDescription,
  pagination,
  testIdPrefix,
  tableTestId,
  // Layout props (all optional)
  alwaysVisible,
  horizontalScroll,
  // Selection props (all optional)
  selectable,
  selectedIds,
  onSelectionChange,
  rowIdField,
  selectionActions,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const selectedBg = alpha(theme.palette.primary.main, 0.08);

  const getRowId = useCallback((row) => row[rowIdField || 'id'], [rowIdField]);

  // Optimistic local selection state for instant checkbox feedback.
  // The ref always holds the latest value so rapid clicks don't hit stale closures.
  const [localSelectedIds, setLocalSelectedIds] = useState(() => selectedIds || new Set());
  const localRef = useRef(selectedIds || new Set());

  useEffect(() => {
    const next = selectedIds || new Set();
    setLocalSelectedIds(next);
    localRef.current = next;
  }, [selectedIds]);

  const allOnPageSelected = selectable && data.length > 0 && data.every(row => localSelectedIds?.has(getRowId(row)));
  const someOnPageSelected = selectable && data.length > 0 && data.some(row => localSelectedIds?.has(getRowId(row)));

  const handleRowToggle = (row) => {
    if (!onSelectionChange) return;
    const id = getRowId(row);
    const next = new Set(localRef.current);
    if (next.has(id)) next.delete(id); else next.add(id);
    setLocalSelectedIds(next);
    localRef.current = next;
    startTransition(() => { onSelectionChange(next); });
  };

  const handleSelectAllToggle = () => {
    if (!onSelectionChange) return;
    const next = new Set(localRef.current);
    if (allOnPageSelected) {
      data.forEach(row => next.delete(getRowId(row)));
    } else {
      data.forEach(row => next.add(getRowId(row)));
    }
    setLocalSelectedIds(next);
    localRef.current = next;
    startTransition(() => { onSelectionChange(next); });
  };

  const showLoadingOverlay = loading && data.length > 0;
  const showLoadingCenter = loading && data.length === 0;
  const showEmpty = !loading && data.length === 0;

  return (
    <Paper elevation={0} sx={{ overflow: 'hidden', ...(!alwaysVisible && { display: { xs: 'none', lg: 'block' } }), position: 'relative' }} data-testid={tableTestId}>
      {/* Loading overlay for refetch */}
      {showLoadingOverlay && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 1,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            pt: 8,
          }}
        >
          <CircularProgress size={32} />
        </Box>
      )}

      {/* Selection toolbar — always reserves space when selectable to prevent layout shift */}
      {selectable && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 3,
            py: 1.5,
            bgcolor: 'background.paper',
            borderBottom: 1,
            borderColor: 'divider',
            height: 52,
          }}
          data-testid="datatable-selection-toolbar"
        >
          {localSelectedIds?.size > 0 && (
            <>
              <Chip
                label={t('common.selectedCount', { count: localSelectedIds.size })}
                size="small"
                color="primary"
                variant="outlined"
              />
              {selectionActions && selectionActions.length > 0 && (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {selectionActions.map((action, idx) => {
                    const btn = (
                      <Button
                        key={idx}
                        size="small"
                        variant={action.variant || 'contained'}
                        color={action.color || 'primary'}
                        startIcon={action.icon}
                        onClick={action.onClick}
                        disabled={action.disabled}
                        sx={{
                          borderRadius: '6px',
                          textTransform: 'none',
                          fontWeight: 600,
                          fontSize: '12px',
                          py: 0.5,
                          px: 1.5,
                        }}
                      >
                        {action.label}
                      </Button>
                    );
                    if (action.tooltip && action.disabled) {
                      return (
                        <Tooltip key={idx} title={action.tooltip} arrow>
                          <span>{btn}</span>
                        </Tooltip>
                      );
                    }
                    return btn;
                  })}
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      <Box sx={horizontalScroll ? { overflowX: 'auto' } : undefined}>
      {showLoadingCenter ? (
        <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress size={32} />
        </Box>
      ) : showEmpty ? (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          {emptyIcon && React.cloneElement(emptyIcon, {
            sx: { fontSize: 48, color: 'text.disabled', mb: 1, ...(emptyIcon.props.sx || {}) },
          })}
          {emptyTitle && (
            <Typography color="text.secondary">{emptyTitle}</Typography>
          )}
          {emptyDescription && (
            <Typography variant="body2" color="text.disabled" sx={{ mt: 1 }}>
              {emptyDescription}
            </Typography>
          )}
        </Box>
      ) : (
        <Box
          component="table"
          sx={{
            width: '100%',
            borderCollapse: 'collapse',
            tableLayout: 'auto',
          }}
        >
          <Box component="thead">
            <Box
              component="tr"
              sx={{
                bgcolor: 'background.default',
              }}
            >
              {selectable && (
                <Box
                  component="th"
                  sx={{
                    p: '14px 8px 14px 12px',
                    width: '1%',
                    borderBottom: 1,
                    borderColor: 'divider',
                    pl: 3,
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={allOnPageSelected}
                    indeterminate={someOnPageSelected && !allOnPageSelected}
                    onChange={handleSelectAllToggle}
                    data-testid={testIdPrefix ? `${testIdPrefix}-select-all` : 'datatable-select-all'}
                  />
                </Box>
              )}
              {columns.map((col) => (
                <Box
                  key={col.key}
                  component="th"
                  sx={{
                    p: '14px 12px',
                    textAlign: col.align || 'left',
                    borderBottom: 1,
                    borderColor: 'divider',
                    whiteSpace: 'nowrap',
                    ...(col.width ? { width: col.width } : {}),
                    ...(col.maxWidth ? { maxWidth: col.maxWidth } : {}),
                    ...(!selectable && { '&:first-of-type': { pl: 3 } }),
                    '&:last-of-type': { pr: 3 },
                    ...(col.hideBelow ? { display: { xs: 'none', [col.hideBelow]: 'table-cell' } } : {}),
                  }}
                >
                  {col.sortable ? (
                    <SortableHeaderCell
                      label={col.label}
                      field={col.sortField || col.key}
                      currentSort={sortField}
                      currentDirection={sortDirection}
                      onSort={onSort}
                      align={col.align}
                    />
                  ) : (
                    <Typography sx={{ ...headerCellSx, textAlign: col.align || 'left' }}>
                      {col.label}
                    </Typography>
                  )}
                </Box>
              ))}
            </Box>
          </Box>
          <Box component="tbody">
            {data.map((row, index) => (
              <Box
                key={row.id || index}
                component="tr"
                data-testid={testIdPrefix ? `${testIdPrefix}-${row.id}` : undefined}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                sx={{
                  cursor: onRowClick ? 'pointer' : 'default',
                  transition: 'background-color 0.15s ease',
                  bgcolor: selectable && localSelectedIds?.has(getRowId(row)) ? selectedBg : 'transparent',
                  '&:hover': onRowClick ? { bgcolor: 'background.default' } : {},
                  '& > td': {
                    borderBottom: index < data.length - 1 ? 1 : 0,
                    borderColor: 'divider',
                  },
                }}
              >
                {selectable && (
                  <Box
                    component="td"
                    sx={{
                      p: '16px 8px 16px 12px',
                      width: '1%',
                      verticalAlign: 'middle',
                      pl: 3,
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={localSelectedIds?.has(getRowId(row)) || false}
                      onChange={(e) => {
                        e.stopPropagation();
                        handleRowToggle(row);
                      }}
                      onClick={(e) => e.stopPropagation()}
                      data-testid={testIdPrefix ? `${testIdPrefix}-select-${getRowId(row)}` : undefined}
                    />
                  </Box>
                )}
                {columns.map((col) => (
                  <Box
                    key={col.key}
                    component="td"
                    sx={{
                      p: '16px 12px',
                      textAlign: col.align || 'left',
                      verticalAlign: 'middle',
                      whiteSpace: col.noWrap ? 'nowrap' : 'normal',
                      ...(col.maxWidth ? { maxWidth: col.maxWidth } : {}),
                      ...(!selectable && { '&:first-of-type': { pl: 3 } }),
                      '&:last-of-type': { pr: 3 },
                      ...(col.hideBelow ? { display: { xs: 'none', [col.hideBelow]: 'table-cell' } } : {}),
                    }}
                  >
                    {col.maxWidth ? (
                      <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0, '& > *': { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }}>
                        {col.render(row, index)}
                      </Box>
                    ) : (
                      col.render(row, index)
                    )}
                  </Box>
                ))}
              </Box>
            ))}
          </Box>
        </Box>
      )}
      </Box>

      {/* Pagination */}
      {pagination && !loading && data.length > 0 && (
        <Box sx={{ px: 3, py: 2, borderTop: 1, borderColor: 'divider' }}>
          <PaginationControls
            page={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            pageSize={pagination.pageSize}
            onPageChange={pagination.onPageChange}
            onPageSizeChange={pagination.onPageSizeChange}
            loading={loading}
          />
        </Box>
      )}
    </Paper>
  );
};

// Custom comparator: columns are inline arrays with new render-function refs every
// render, so compare by key list only. selectionActions are also inline arrays, so
// compare by label+disabled. Everything else uses reference equality.
function dataTablePropsAreEqual(prev, next) {
  // Fast path — same reference
  if (prev === next) return true;

  // Compare columns by key list
  const pc = prev.columns, nc = next.columns;
  if (pc !== nc) {
    if (!pc || !nc || pc.length !== nc.length) return false;
    for (let i = 0; i < pc.length; i++) {
      if (pc[i].key !== nc[i].key) return false;
    }
  }

  // Compare selectionActions by label + disabled
  const pa = prev.selectionActions, na = next.selectionActions;
  if (pa !== na) {
    if (!pa || !na || pa.length !== na.length) return false;
    for (let i = 0; i < pa.length; i++) {
      if (pa[i].label !== na[i].label || pa[i].disabled !== na[i].disabled) return false;
    }
  }

  // Compare selectedIds by size (local state handles rendering, this is for sync effect)
  const ps = prev.selectedIds, ns = next.selectedIds;
  if (ps !== ns) {
    if (!ps || !ns || ps.size !== ns.size) return false;
    for (const id of ps) {
      if (!ns.has(id)) return false;
    }
  }

  // Shallow-compare remaining props
  const skip = new Set(['columns', 'selectionActions', 'selectedIds']);
  const allKeys = new Set([...Object.keys(prev), ...Object.keys(next)]);
  for (const key of allKeys) {
    if (skip.has(key)) continue;
    if (prev[key] !== next[key]) return false;
  }
  return true;
}

export default React.memo(DataTable, dataTablePropsAreEqual);
